import {
  AfterViewInit,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, take } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '@core/services/api/api.service';
import { CustomDialogService } from '@core/services/custom-dialog.service';
import {
  TransactionViewSourceService,
  type TransactionViewSourceConfig,
} from '@core/services/transaction-view-source.service';
import { KlTransactionGrid } from './kl-transaction-grid/kl-transaction-grid';
import {
  ActionDto,
  FilterPayload,
  FilterableItem,
  GridColumn,
} from '../kl-grid/grid.models';
import { readUserEnvIds } from '../../../layout/pages/material-management/material-env-filter';
import type { KlTransactionDataMode, KlTransactionDetailFilterDialogOptions } from './kl-transaction.models';

interface TransactionRow {
  id?: number | string;
  module_type?: string;
  cancelled?: string;
  [key: string]: unknown;
}

@Component({
  selector: 'kl-transaction',
  standalone: false,
  templateUrl: './kl-transaction.html',
  styleUrl: './kl-transaction.scss',
})
export class KlTransaction implements AfterViewInit {
  private readonly api = inject(ApiService);
  private readonly customDialog = inject(CustomDialogService);
  private readonly translate = inject(TranslateService);
  private readonly viewSourceService = inject(TransactionViewSourceService);
  private readonly destroyRef = inject(DestroyRef);

  readonly localGrid = viewChild(KlTransactionGrid);

  readonly columns = input.required<GridColumn[]>();
  readonly authNum = input.required<string>();
  readonly recordId = input<number | string | null>(null);
  readonly filterKey = input('');
  readonly endpoint = input.required<string>();
  readonly endpointName = input('');
  readonly dataMode = input<KlTransactionDataMode>('client');
  /** Toolbar page-size value at or above this triggers a single “fetch all” request; grid pages locally at this size. */
  readonly allRowsPageSize = input(999);
  /** Backend `query.paging.limit` used when {@link allRowsPageSize} is selected (transaction endpoints cap row count, not pages). */
  readonly allRowsLimit = input(999999);
  readonly clientDataPath = input('');
  readonly extraFilter = input<Record<string, unknown>>({});
  readonly designParentModule = input('');
  readonly designModule = input('');
  readonly designKey = input('');
  readonly title = input('');
  readonly titleSuffix = input('');
  readonly filterSelectItems = input<FilterableItem[]>([]);
  readonly mapCancelled = input(false);
  readonly mapRowsInput = input<((rows: unknown[]) => unknown[]) | null>(null, {
    alias: 'mapRows',
  });
  readonly detailFilterComponent = input<unknown>(null);
  readonly detailFilterDialogOptions = input<KlTransactionDetailFilterDialogOptions | null>(null);
  readonly viewSourceConfig = input<TransactionViewSourceConfig | null>(null);
  readonly extraActions = input<ActionDto[]>([], { alias: 'actions' });
  readonly fullscreen = input(true);
  readonly doExport = input(true);
  readonly doOther = input(true);
  readonly sortable = input(true);
  readonly deferResizing = input(true);
  readonly customFilter = input(true);
  readonly filterable = input(true);
  readonly hasFilter = input(false);
  /** Columns summed in the grid footer and the Excel totals row. */
  readonly footerSumColumns = input<readonly string[]>([]);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly clientRows = signal<unknown[]>([]);
  readonly clientFilterPayload = signal<FilterPayload | null>(null);

  readonly dialogName = computed(() => {
    const base = this.title() || this.translate.instant('transactions');
    const suffix = this.titleSuffix();
    return suffix ? `${base} - ${suffix}` : base;
  });

  readonly resolvedMapRows = computed(() => {
    const custom = this.mapRowsInput();
    if (custom) return custom;
    if (!this.mapCancelled()) return null;
    return (rows: TransactionRow[]) =>
      rows.map((row) => {
        const next = { ...row };
        if (next.cancelled === 'YES') {
          next.cancelled = 'İ';
        } else if (next.cancelled === 'NO') {
          next.cancelled = '';
        }
        return next;
      });
  });

  readonly clientMapRows = computed((): ((rows: unknown[]) => unknown[]) | null => {
    const fn = this.resolvedMapRows();
    if (!fn) return null;
    return (rows) => fn(rows as TransactionRow[]);
  });

  readonly actions = computed<ActionDto[]>(() => {
    const list = [...this.extraActions()];
    const vs = this.viewSourceConfig();
    if (vs) {
      list.unshift({
        slug: 'view_source',
        icon: 'ph ph-eye',
        notShowInToolbar: true,
        method: (ctx) => this.viewSourceService.open(vs, ctx.item as TransactionRow, this.gridRef()),
      });
    }
    return list;
  });

  readonly showDetailFilter = computed(() => !!this.detailFilterComponent());

  ngAfterViewInit(): void {
    // Grids with designModule restore a saved fast filter and call List themselves — avoid a duplicate initial fetch.
    if (!this.designModule().trim()) {
      this.reload();
    }
  }

  reload(): void {
    this.loadClientData(this.clientFilterPayload(), this.localGrid()?.pageSize() ?? 10);
  }

  /** List / fast filter from kl-transaction-grid → refetch with toolbar fetch limit. */
  onGridFilter(event: FilterPayload | { filterInstance?: FilterPayload }): void {
    const filterInstance =
      event != null && typeof event === 'object' && 'filterInstance' in event && event.filterInstance != null
        ? event.filterInstance
        : (event as FilterPayload);
    this.clientFilterPayload.set(filterInstance);
    const limit = Number(filterInstance?.query?.paging?.limit);
    const pageSize = Number.isFinite(limit) && limit > 0 ? limit : (this.localGrid()?.pageSize() ?? 10);
    this.loadClientData(filterInstance, pageSize);
  }

  openDetailFilter(event: { pageSize?: number }): void {
    const component = this.detailFilterComponent();
    const options = this.detailFilterDialogOptions();
    if (!component || !options) return;

    const ref = this.customDialog.open({
      modalId: options.modalId,
      component: component as never,
      header: options.header ?? this.translate.instant('detail_filter'),
      width: options.width ?? '100vw',
      styleClass: options.styleClass ?? 'kl-detail-filter-dialog-shell',
      contentStyle: options.contentStyle ?? {
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '0',
      },
      position: options.position ?? 'bottom',
      transitionOptions: options.transitionOptions ?? '280ms cubic-bezier(0.22, 1, 0.36, 1)',
      data: { pageSize: event?.pageSize },
    });

    ref?.onClose.pipe(take(1)).subscribe((result: unknown) => {
      const r = result as { filterInstance?: FilterPayload } | undefined;
      if (!r?.filterInstance) return;
      this.applyFilter(r.filterInstance);
    });
  }

  applyFilter(filterInstance: FilterPayload): void {
    this.clientFilterPayload.set(filterInstance);
    this.loadClientData(filterInstance, this.localGrid()?.pageSize() ?? 10);
  }

  /** Export all rows for the current filter (not list page size / table paginator page). */
  onExport(type: string): void {
    const t = type.trim().toLowerCase();
    if (t !== 'xlsx' && t !== 'excel') {
      return;
    }
    const grid = this.localGrid();
    if (!grid) {
      return;
    }

    const exportLoaded = (): void => grid.exportToXlsx();
    const listLimit = grid.pageSize();
    if (listLimit >= this.allRowsPageSize()) {
      exportLoaded();
      return;
    }

    grid.showLoading('loading');
    this.fetchClientRows(this.clientFilterPayload(), this.allRowsPageSize())
      .pipe(
        take(1),
        finalize(() => grid.hideLoading())
      )
      .subscribe({
        next: (rows) => grid.exportToXlsx(rows),
        error: () => this.loadError.set(true),
      });
  }

  /** Maps toolbar page size to API limit: N → N rows; “all” page size → {@link allRowsLimit}. */
  private backendFetchLimit(pageSize: number): number {
    if (pageSize >= this.allRowsPageSize()) {
      return this.allRowsLimit();
    }
    return pageSize;
  }

  /** Loads rows into {@link clientRows}; kl-transaction-grid pages the full set on the frontend. */
  private loadClientData(filterPayload: FilterPayload | null, pageSize: number): void {
    const txGrid = this.localGrid();
    if (txGrid) {
      txGrid.showLoading('loading');
    } else {
      this.loading.set(true);
    }
    this.loadError.set(false);
    this.fetchClientRows(filterPayload, pageSize)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          const g = this.localGrid();
          if (g) {
            g.hideLoading();
          } else {
            this.loading.set(false);
          }
        })
      )
      .subscribe({
        next: (rows) => this.clientRows.set(rows),
        error: () => this.loadError.set(true),
      });
  }

  private fetchClientRows(filterPayload: FilterPayload | null, pageSize: number): Observable<unknown[]> {
    const id = this.recordId();
    const key = this.filterKey();
    if (key && (id == null || id === '')) {
      this.loadError.set(true);
      return EMPTY;
    }

    const baseFilter: Record<string, unknown> = {
      ...readUserEnvIds(),
      ...(this.extraFilter() ?? {}),
    };
    if (key && id != null) {
      baseFilter[key] = { id };
    }

    const mergedFilter = {
      ...baseFilter,
      ...(filterPayload?.filter ?? {}),
    };

    const fetchLimit = this.backendFetchLimit(pageSize);

    const body: FilterPayload = {
      filter: mergedFilter,
      query: {
        sort: [{ name: 'id', desc: true }],
        ...filterPayload?.query,
        paging: { limit: fetchLimit, offset: 0, fetch: 'EAGER' },
      },
      parameters: filterPayload?.parameters ?? [],
    };

    const path = this.clientDataPath();
    return this.api.postSpecialFilter<unknown>(this.endpoint(), body).pipe(
      map((res) => {
        let data: unknown = (res as { data?: unknown })?.data;
        if (path && data != null && typeof data === 'object') {
          data = (data as Record<string, unknown>)[path];
        }
        return Array.isArray(data) ? data : [];
      })
    );
  }

  private gridRef(): {
    showLoading(text: string): void;
    hideLoading(): void;
    handleError(error: unknown): void;
  } {
    const g = this.localGrid();
    if (g) return g;
    return {
      showLoading: () => undefined,
      hideLoading: () => undefined,
      handleError: () => undefined,
    };
  }
}
